<template>
    <card title="PV1 Queue">
        <b-row align-v="end">
            <b-col cols="12" lg="5">
                <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate clearable
                    :disabled="isLoading" @input="resetPageAndLoad" />
            </b-col>
            <b-col cols="12" lg="5">
                <label class="mb-0">Program:</label>
                <b-dropdown ref="programDropdown" :text="programDropdownLabel"
                    variant="outline-primary" block
                    class="program-dropdown" @shown="onProgramDropdownShown">
                    <b-dropdown-form class="sticky-top bg-white" style="top: -10px;"
                        form-class="program-search-form">
                        <b-form-input ref="programSearch" v-model="programSearchText"
                            placeholder="Search programs..." size="sm" class="glass-pane"
                            @keydown.down.prevent="highlightNext"
                            @keydown.up.prevent="highlightPrev"
                            @keydown.enter.prevent="selectHighlighted" />
                    </b-dropdown-form>
                    <b-dropdown-form v-for="(program, index) in visiblePrograms" :key="program.id"
                        :class="['py-1', { 'program-highlighted': index === highlightedIndex }]">
                        <div class="d-flex align-items-center">
                            <b-form-checkbox
                                :checked="programFilters.some(p => p.id === program.id)"
                                @change="toggleProgramCheckbox(program)"
                                class="mr-2" />
                            <span @click.stop="selectOnlyProgram(program)"
                                class="flex-grow-1"
                                :style="{ cursor: 'pointer', color: programCounts[program.id] ? undefined : '#adb5bd' }">
                                {{ program.name }}
                            </span>
                            <b-badge v-if="programCounts[program.id]" variant="primary"
                                class="ml-1">{{ programCounts[program.id] }}</b-badge>
                        </div>
                    </b-dropdown-form>
                    <b-dropdown-divider />
                    <b-dropdown-item v-if="programFilters.length < allPrograms.length"
                        @click="selectAllPrograms">
                        Select All
                    </b-dropdown-item>
                    <b-dropdown-item v-if="programFilters.length > 0"
                        @click="clearProgramFilters">
                        Clear Selection
                    </b-dropdown-item>
                </b-dropdown>
            </b-col>
            <b-col cols="2">
                <b-form-checkbox v-model="includeOnHold" @input="resetPageAndLoad"
                    v-b-tooltip.hover.right="onHoldTooltip">
                    Include On Hold
                </b-form-checkbox>
                <b-form-checkbox v-model="includeLocked" @input="resetPageAndLoad">
                    Include Locked
                </b-form-checkbox>
                <b-form-checkbox v-model="includeAwaitingInsurance" @input="resetPageAndLoad">
                    Include Awaiting Insurance
                </b-form-checkbox>
            </b-col>
        </b-row>
        <b-row v-if="openErrorCount">
            <b-col>
                <Checkbox v-model="showOpenDispense" @input="resetPageAndLoad">
                    Open {{dispenseErrorLabel + "s"}} ({{ openErrorCount }})
                </Checkbox>
            </b-col>
        </b-row>

        <PrescriptionsListComponent ref="prescriptionsList" :defaultSort="currentSort"
            :prescriptions="prescriptions" :additionalFields="additionalFields" :action-buttons="actionButtons"
            :is-loading="isLoading" :show-void-box="false"
            :manualSort="['created', 'productNameShort', 'patientName', 'fillDate', 'storeName', 'programName']"
            show-select-checkox show-patient-name :colorCodes="handleRowStyles"
            @selection-changed="selectionChanged" @sort-changed="handleSort">
        </PrescriptionsListComponent>

        <BottomPagerBar v-model="pageNumber" :total-rows="totalRows"
            :per-page="pageSize" show-total
            :selected-rows="selectedPrescriptions.length"
            show-page-size-selector item-name="prescription" @change-page-size="handlePageSizeChange"
            @clear-selection="clearSelection"
            :loading="isLoading">
            <template #left-after>
                <b-button size="sm" class="btn-auto-mode" @click="processNext"
                    :disabled="isLoading || selectedPrescriptions.length > 0">
                    <i class="fa fa-rocket"></i> Enter Auto-Mode for PV1
                </b-button>
            </template>
            <template #fab>
                <b-button v-if="selectedPrescriptions.length > 0" variant="success" @click="openBatchVerification" :disabled="isLoading">
                    Process Selected
                </b-button>
            </template>
        </BottomPagerBar>
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import { DatePicker } from "element-ui";
import SearchComponent from '@/components/SearchComponent.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Prescription } from '@/models/Prescription';
import { Store } from '@/models/Store';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import axios, { AxiosError, CancelTokenSource } from 'axios';
import moment from "moment/moment";
import { Program } from "@/models/Program";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import AutoModeService from '@/services/AutoModeService';
import { saveQueueFilters, loadQueueFilters } from '@/util/queueFilterPersistence';

@Component({
    name: "PV1Queue",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        Checkbox,
        BottomPagerBar,
    },
})
export default class PV1Queue extends Mixins(AutoModeHotkeyMixin) {
    protected prescriptions: Array<Prescription> = [];
    protected selectedPrescriptions: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private programFilters: Program[] = [];
    protected allPrograms: Program[] = [];
    private programSearchText = '';
    private highlightedIndex = 0;
    private programCounts: Record<number, number> = {};

    protected isLoading = false;
    protected actionButtons = ['pv1'];
    protected showOpenDispense = false;
    protected includeOnHold = false;
    private onHoldCount = 0;
    protected includeLocked = false;
    protected includeAwaitingInsurance = false;

    protected totalRows: number = 0;
    protected totalErrorRows: number = 0;
    protected pageNumber: number = 1;
    protected pageSize: number = 100;
    protected currentBatchIndex: number = 0;
    protected currentSort: string = "created";
    private sortBy: string = "created";
    private sortDirection: string = "A";
    private cancelTokenSource: CancelTokenSource | null = null;

    get additionalFields() {
        const fields = [
            {
                index: 9,
                field: {
                    key: "created",
                    label: "Created",
                    sortable: true,
                    sortDirection: "asc",
                    formatter: this.dateFormatter,
                },
            }
        ];

        if (this.includeLocked) {
            fields.push({
                index: 10,
                field: {
                    key: "locked",
                    label: "Locked By",
                    sortable: false,
                    class: "locked",
                },
            });
        }

        return fields;
    };

    dateFormatter(value: Date, _key: string, _item: any) {
        return moment.utc(value).format("L");
    }

    handleRowStyles(item: any, type: any) {
        if (!item || type !== 'row') return;

        if (item.lockedBy) {
            return "bg-warning locked";
        }

        return "";
    }

    private skipFilterSave = false;

    async mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_PV1Queue', 100);
        this.restoreFilters();
        this.loadPrograms(); // fire-and-forget — populates dropdown independently
        this.loadOnHoldCount();
        this.load();
        this.fetchDispenseErrorLabel();
    }

    loadProgramCounts() {
        const params = {
            storeId: this.storeFilter?.id || undefined,
            includeOnHold: this.includeOnHold,
            includeLocked: this.includeLocked,
            includeAwaitingInsurance: this.includeAwaitingInsurance,
            excludeDispenseErrors: !this.showOpenDispense,
        };
        this.$http.get<any[]>('api/prescription/pv1-queue/program-counts', { params })
            .then(response => {
                const counts: Record<number, number> = {};
                for (const item of response.data) {
                    counts[item.programID] = item.count;
                }
                this.programCounts = counts;
            })
            .catch(err => {
                console.error("Error loading program counts", err);
            });
    }

    private restoreFilters() {
        const saved = loadQueueFilters(this, 'pv1Queue_filters');
        if (!saved) return;
        this.skipFilterSave = true;
        if (saved.store?.id) this.storeFilter = new Store(saved.store);
        if (Array.isArray(saved.programIds)) {
            // Program objects will be restored after loadPrograms completes
            this._savedProgramIds = saved.programIds;
        }
        if (typeof saved.includeOnHold === 'boolean') this.includeOnHold = saved.includeOnHold;
        if (typeof saved.includeLocked === 'boolean') this.includeLocked = saved.includeLocked;
        if (typeof saved.includeAwaitingInsurance === 'boolean') this.includeAwaitingInsurance = saved.includeAwaitingInsurance;
        if (typeof saved.showOpenDispense === 'boolean') this.showOpenDispense = saved.showOpenDispense;
        this.$nextTick(() => { this.skipFilterSave = false; });
    }

    private _savedProgramIds: number[] = [];

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveQueueFilters(this, 'pv1Queue_filters', {
            store: this.storeFilter?.id ? { id: this.storeFilter.id, name: this.storeFilter.name } : null,
            programIds: this.programFilters.map(p => p.id),
            includeOnHold: this.includeOnHold,
            includeLocked: this.includeLocked,
            includeAwaitingInsurance: this.includeAwaitingInsurance,
            showOpenDispense: this.showOpenDispense,
        });
    }

    async loadOnHoldCount() {
        try {
            const resp = await this.$http.get('api/prescription/pv1-queue/on-hold-count');
            this.onHoldCount = resp.data;
        } catch {
            this.onHoldCount = 0;
        }
    }

    async loadPrograms() {
        try {
            const resp = await this.$http.get('/Program', { params: { active: true } });
            this.allPrograms = resp.data.map((p: any) => new Program(p));
            // Restore saved program selections after programs are loaded
            if (this._savedProgramIds?.length) {
                this.skipFilterSave = true;
                this.programFilters = this.allPrograms.filter(p => this._savedProgramIds.includes(p.id));
                this._savedProgramIds = [];
                this.$nextTick(() => { this.skipFilterSave = false; });
            }
        } catch (err) {
            console.error("Error loading programs", err);
            this.$notification(NotificationOptions.error("Failed to load programs"));
        }
    }

    get visiblePrograms(): Program[] {
        if (!this.programSearchText) return this.allPrograms;
        const search = this.programSearchText.toLowerCase();
        return this.allPrograms.filter(p => p.name.toLowerCase().includes(search));
    }

    onProgramDropdownShown() {
        this.programSearchText = '';
        this.highlightedIndex = 0;
        setTimeout(() => {
            (this.$refs.programSearch as any)?.focus();
        }, 50);
    }

    @Watch('programSearchText')
    onProgramSearchChanged() {
        this.highlightedIndex = 0;
    }

    highlightNext() {
        if (this.highlightedIndex < this.visiblePrograms.length - 1) {
            this.highlightedIndex++;
        }
    }

    highlightPrev() {
        if (this.highlightedIndex > 0) {
            this.highlightedIndex--;
        }
    }

    selectHighlighted() {
        if (this.visiblePrograms.length === 0) return;
        const program = this.visiblePrograms[this.highlightedIndex];
        if (program) {
            this.toggleProgramCheckbox(program);
        }
    }

    get programDropdownLabel(): string {
        if (this.programFilters.length === 0) return 'All Programs';
        return this.programFilters.map(p => p.name).sort().join(', ');
    }

    selectOnlyProgram(program: Program) {
        if (this.programFilters.length === 1 && this.programFilters[0].id === program.id) {
            this.programFilters = [];
        } else {
            this.programFilters = [program];
        }
        (this.$refs.programDropdown as any)?.hide();
    }

    toggleProgramCheckbox(program: Program) {
        const index = this.programFilters.findIndex(p => p.id === program.id);
        if (index >= 0) {
            this.programFilters.splice(index, 1);
        } else {
            this.programFilters.push(program);
        }
    }

    selectAllPrograms() {
        this.programFilters = [...this.allPrograms];
    }

    clearProgramFilters() {
        this.programFilters = [];
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_PV1Queue', this.pageSize);
    }

    @Watch('programFilters')
    onProgramFiltersChanged() {
        this.persistFilters();
        this.resetPageAndLoad();
    }

    selectionChanged(selectedPrescriptions: Array<Prescription>) {
        this.selectedPrescriptions = selectedPrescriptions;
    }

    resetPageAndLoad() {
        this.persistFilters();
        if (this.pageNumber === 1) {
            this.load();
        } else {
            this.pageNumber = 1;
        }
    }

    @Watch("pageNumber")
    load() {
        this.loadProgramCounts();

        this.cancelTokenSource?.cancel();
        const source = axios.CancelToken.source();
        this.cancelTokenSource = source;

        this.isLoading = true;

        const params = {
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
            includeOnHold: this.includeOnHold,
            includeLocked: this.includeLocked,
            includeAwaitingInsurance: this.includeAwaitingInsurance,
            storeId: this.storeFilter?.id || undefined,
            programIds: this.programFilters.length > 0 ? this.programFilters.map(p => p.id).join(',') : undefined,
            sortBy: this.sortBy,
            sortDirection: this.sortDirection,
            excludeDispenseErrors: !this.showOpenDispense,
        };

        window.scrollTo({ top: 0, behavior: 'smooth' });

        this.$http.get<any>('api/prescription/pv1-queue', { params, cancelToken: source.token })
            .then(response => {
                // Populate patientName for sorting since API returns separate first/last name fields
                this.prescriptions = (response.data.scripts || []).map((rx: any) => {
                    if (!rx.patientName && (rx.patientLastName || rx.patientFirstName)) {
                        rx.patientName = `${rx.patientLastName || ''}, ${rx.patientFirstName || ''}`.trim();
                    }
                    return rx;
                });
                this.totalRows = response.data.totalRows;
                this.totalErrorRows = response.data.totalErrorRows ?? 0;
            })
            .catch((error: AxiosError) => {
                if (axios.isCancel(error)) return;
                console.error("Error while loading PV1 queue.", { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                if (this.cancelTokenSource === source) {
                    this.isLoading = false;
                }
            });
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        if (this.pageNumber === 1) {
            this.load();
        } else {
            this.pageNumber = 1;
        }
    }

    handlePageSizeChange(newSize: number) {
        this.pageSize = newSize;
        if (this.pageNumber === 1) {
            this.load();
        } else {
            this.pageNumber = 1;
        }
    }

    get onHoldTooltip(): string {
        return `${this.onHoldCount} on hold prescription${this.onHoldCount !== 1 ? 's' : ''} pending PV1`;
    }

    get openErrorCount() {
        return this.totalErrorRows;
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }

    clearSelection() {
        this.selectedPrescriptions = [];
        // Clear the child component's selection state
        if (this.$refs.prescriptionsList) {
            (this.$refs.prescriptionsList as any).prescriptionsSelected = [];
        }
    }

    processNext() {
        AutoModeService.saveSpecificQueues(['PV1']);
        this.$store.autoModeEnabled = true;
        this.handleAutoModeNextItem(undefined, undefined, { queues: ['PV1'] });
    }

    openBatchVerification() {
        if (this.selectedPrescriptions.length === 0) {
            this.$notification(NotificationOptions.warning("Please select at least one prescription"));
            return;
        }

        // Store selected prescriptions in the global store (similar to ingestion queue)
        const firstPrescription = this.selectedPrescriptions[0];
        const remainingPrescriptions = this.selectedPrescriptions.slice(1);

        // Store remaining prescriptions in the store for batch processing
        this.$store.pv1BatchPrescriptions = remainingPrescriptions;

        // Navigate to the first prescription in the batch
        this.$router.push({
            name: 'PV1',
            params: {
                rxId: firstPrescription.rxID?.toString() || '',
                storeID: firstPrescription.storeID?.toString() || '',
                rxNumber: firstPrescription.rxNumber?.toString() || '',
                rfNumber: firstPrescription.rfNumber?.toString() || ''
            },
            query: {
                batch: remainingPrescriptions.length > 0 ? 'true' : 'false',
            }
        });
    }

}

</script>

<style scoped>
.program-dropdown ::v-deep(.dropdown-toggle) {
    height: calc(1.5em + 0.75rem + 2px);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.program-dropdown ::v-deep(.dropdown-menu) {
    max-height: 500px;
    overflow-y: auto;
}

.program-dropdown ::v-deep(.b-dropdown-form:not(.program-search-form):hover),
.program-dropdown ::v-deep(.program-highlighted > .b-dropdown-form) {
    background-color: #f8f9fa;
}

::v-deep(.locked) {
    width: 200px;
}

.btn-auto-mode {
    background-color: #ff6b35;
    border-color: #ff6b35;
    color: white;
}

.btn-auto-mode:hover:not(:disabled) {
    background-color: #e85e2b;
    border-color: #e85e2b;
    color: white;
}

.btn-auto-mode:disabled {
    background-color: #ff6b35;
    border-color: #ff6b35;
    opacity: 0.65;
}
</style>
